import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import api from '../utils/api';

const Prices = () => {
  const { language, isRTL } = useLanguage();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCarType, setSelectedCarType] = useState('all');
  const [selectedPickup, setSelectedPickup] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const res = await api.get('/route-presets');
        setPresets(res.data);
      } catch (error) {
        console.error('Error fetching presets:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPresets();
  }, []);

  // Get unique car types and pickup locations
  const carTypes = useMemo(() => {
    const types = [...new Set(presets.map(p => p.carType))].filter(t => t !== 'all');
    return ['all', ...types];
  }, [presets]);

  const pickupLocations = useMemo(() => {
    return ['all', ...new Set(presets.map(p => p.pickupLocation))].sort();
  }, [presets]);

  // Filter presets
  const filteredPresets = useMemo(() => {
    let filtered = presets;

    // Filter by car type
    if (selectedCarType !== 'all') {
      filtered = filtered.filter(p => p.carType === selectedCarType || p.carType === 'all');
    }

    // Filter by pickup location
    if (selectedPickup !== 'all') {
      filtered = filtered.filter(p => p.pickupLocation === selectedPickup);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(p =>
        p.pickupLocation.toLowerCase().includes(query) ||
        p.dropoffLocation.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [presets, selectedCarType, selectedPickup, searchQuery]);

  // Group presets by pickup location
  const groupedPresets = useMemo(() => {
    const groups = {};
    filteredPresets.forEach(preset => {
      const key = preset.pickupLocation;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(preset);
    });
    return groups;
  }, [filteredPresets]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ar-EG', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const getCarTypeLabel = (carType) => {
    if (carType === 'all') return language === 'en' ? 'All Vehicles' : 'جميع السيارات';
    return carType;
  };

  return (
    <div className="min-h-screen bg-blackC">
      {/* Hero Section */}
      <section className="relative pt-32 lg:pt-40 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-dark via-blackC to-blackC" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4 animate-fade-in-up opacity-0 animation-delay-100">
              {language === 'en' ? 'Transparent Pricing' : 'أسعار شفافة'}
            </p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-cream mb-6 animate-fade-in-up opacity-0 animation-delay-200">
              {language === 'en' ? 'Our Prices' : 'أسعارنا'}
            </h1>
            <p className="text-gray-silver text-lg max-w-2xl mx-auto mb-8 animate-fade-in-up opacity-0 animation-delay-300">
              {language === 'en'
                ? 'Explore our competitive pricing for all routes and vehicle types. All prices are transparent and fixed.'
                : 'استكشف أسعارنا التنافسية لجميع المسارات وأنواع المركبات. جميع الأسعار شفافة وثابتة.'}
            </p>
            <div className="divider-gold mx-auto animate-fade-in-up opacity-0 animation-delay-400" />
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 border-b border-gray-dark/50 bg-blackC/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="w-full lg:w-auto lg:flex-1 max-w-md">
              <div className="relative">
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-silver"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={language === 'en' ? 'Search by location...' : 'ابحث حسب الموقع...'}
                  className="w-full bg-dark border border-gray-dark text-cream px-12 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors"
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-silver hover:text-cream transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 w-full lg:w-auto">
              {/* Car Type Filter */}
              <div className="relative flex-1 lg:flex-none min-w-[150px]">
                <select
                  value={selectedCarType}
                  onChange={(e) => setSelectedCarType(e.target.value)}
                  className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors appearance-none cursor-pointer pr-10"
                >
                  {carTypes.map(type => (
                    <option key={type} value={type}>
                      {getCarTypeLabel(type)}
                    </option>
                  ))}
                </select>
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-silver pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Pickup Location Filter */}
              <div className="relative flex-1 lg:flex-none min-w-[150px]">
                <select
                  value={selectedPickup}
                  onChange={(e) => setSelectedPickup(e.target.value)}
                  className="w-full bg-dark border border-gray-dark text-cream px-4 py-3 rounded-lg focus:outline-none focus:border-primary/50 transition-colors appearance-none cursor-pointer pr-10"
                >
                  <option value="all">{language === 'en' ? 'All Locations' : 'جميع المواقع'}</option>
                  {pickupLocations.filter(loc => loc !== 'all').map(location => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-silver pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prices Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : filteredPresets.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-24 h-24 rounded-full bg-dark border border-gray-dark mx-auto mb-6 flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-display text-cream mb-3">
                {language === 'en' ? 'No Prices Found' : 'لا توجد أسعار'}
              </h3>
              <p className="text-gray-silver mb-8">
                {language === 'en'
                  ? 'Try adjusting your filters or search query.'
                  : 'حاول تعديل المرشحات أو استعلام البحث.'}
              </p>
              <button
                onClick={() => {
                  setSelectedCarType('all');
                  setSelectedPickup('all');
                  setSearchQuery('');
                }}
                className="px-6 py-3 bg-primary text-blackC font-semibold rounded-lg hover:bg-primary-light transition-colors"
              >
                {language === 'en' ? 'Clear Filters' : 'مسح المرشحات'}
              </button>
            </div>
          ) : (
            <div className="space-y-12">
              {Object.entries(groupedPresets).map(([pickupLocation, locationPresets], groupIndex) => (
                <div
                  key={pickupLocation}
                  className="animate-fade-in-up opacity-0"
                  style={{ animationDelay: `${groupIndex * 100}ms` }}
                >
                  {/* Pickup Location Header */}
                  <div className="mb-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center">
                        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-3xl font-display text-cream mb-1">
                          {pickupLocation}
                        </h2>
                        <p className="text-gray-silver text-sm">
                          {locationPresets.length} {language === 'en' ? 'routes available' : 'مسار متاح'}
                        </p>
                      </div>
                    </div>
                    <div className="h-px bg-gradient-to-r from-primary/50 via-primary/20 to-transparent" />
                  </div>

                  {/* Routes Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {locationPresets.map((preset, index) => (
                      <div
                        key={preset._id}
                        className="group relative bg-dark border border-gray-dark rounded-xl p-6 hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10"
                        style={{ animationDelay: `${(groupIndex * 100) + (index * 50)}ms` }}
                      >
                        {/* Route Arrow */}
                        <div className="flex items-center gap-3 mb-4">
                          <div className="flex-1">
                            <div className="text-xs text-gray-silver uppercase tracking-wider mb-1">
                              {language === 'en' ? 'From' : 'من'}
                            </div>
                            <div className="text-cream font-medium text-lg">
                              {preset.pickupLocation}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </div>
                          <div className="flex-1 text-right">
                            <div className="text-xs text-gray-silver uppercase tracking-wider mb-1">
                              {language === 'en' ? 'To' : 'إلى'}
                            </div>
                            <div className="text-cream font-medium text-lg">
                              {preset.dropoffLocation}
                            </div>
                          </div>
                        </div>

                        {/* Price & Car Type */}
                        <div className="pt-4 border-t border-gray-dark">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <div className="text-xs text-gray-silver mb-1">
                                {language === 'en' ? 'Price' : 'السعر'}
                              </div>
                              <div className="text-2xl font-display text-primary">
                                {formatPrice(preset.price)}
                              </div>
                            </div>
                            {preset.carType !== 'all' && (
                              <div className="px-3 py-1.5 bg-primary/10 border border-primary/30 rounded-lg">
                                <span className="text-primary text-sm font-medium">
                                  {preset.carType}
                                </span>
                              </div>
                            )}
                            {preset.carType === 'all' && (
                              <div className="px-3 py-1.5 bg-gray-dark/50 border border-gray-dark rounded-lg">
                                <span className="text-gray-silver text-sm font-medium">
                                  {language === 'en' ? 'All Vehicles' : 'جميع السيارات'}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Book Button */}
                          <Link
                            to={`/book?pickup=${encodeURIComponent(preset.pickupLocation)}&dropoff=${encodeURIComponent(preset.dropoffLocation)}`}
                            className="block w-full mt-4 px-4 py-3 bg-gradient-to-r from-primary to-primary-light text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all text-center text-sm"
                          >
                            {language === 'en' ? 'Book This Trip' : 'احجز هذه الرحلة'}
                          </Link>
                        </div>

                        {/* Decorative Corner */}
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primary/5 to-transparent rounded-bl-full pointer-events-none" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      {!loading && filteredPresets.length > 0 && (
        <section className="py-16 border-t border-gray-dark">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-8 lg:p-12">
              <h3 className="text-3xl font-display text-cream mb-4">
                {language === 'en' ? "Don't See Your Route?" : 'لا تجد مسارك؟'}
              </h3>
              <p className="text-gray-silver mb-8 text-lg">
                {language === 'en'
                  ? 'Contact us for custom routes and special pricing. We\'re here to help you plan your perfect journey.'
                  : 'تواصل معنا للمسارات المخصصة والأسعار الخاصة. نحن هنا لمساعدتك في التخطيط لرحلتك المثالية.'}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary to-primary-light text-blackC font-semibold rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {language === 'en' ? 'Contact Us' : 'تواصل معنا'}
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Prices;

